/**
 * @fileoverview Component for displaying a text input.
 * This component includes a label, an input field, and an error message.
 */

import { ExclamationCircleIcon } from '@heroicons/react/20/solid'
import { ChangeEvent } from 'react'


/**
 * @interface TextInputProps
 * @description Props for the TextInput component.
 * @property {string | JSX.Element} label - The label for the input field.
 * @property {string} validationError - The error message to display if the input is invalid.
 * @property {unknown} error - The error object to display if the input is invalid.
 */
interface TextInputProps
    extends React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> {
    label?: string | JSX.Element
    validationError?: string
    error: unknown
}

const normalClasses = 'border-gray-300 focus:border-indigo-500  focus:ring-indigo-500'
const errorClasses =
    'border-red-300 pr-10 text-red-900 focus:border-red-500 focus:outline-none focus:ring-red-500'

/**
 * @function TextInput
 * @description Component for displaying a text input.
 * @param {TextInputProps} props - The props for the component.
 * @returns {JSX.Element} - A React component that renders the text input.
 */
export function TextInput({
    error,
    label,
    validationError,
    className,
    ...attributes
}: TextInputProps) {
    function onChange(e: ChangeEvent<HTMLInputElement>) {
        attributes.onChange?.(e)
    }

    return (
        <div className="relative">
            {label && (
                <div className="mb-1">
                    <label
                        htmlFor="external_id"
                        className="block text-sm font-medium text-gray-700"
                    >
                        {label}
                    </label>
                </div>
            )}
            <input
                {...attributes}
                className={`relative block w-full appearance-none border placeholder:text-sm text-gray-900 placeholder-gray-500 focus:z-10 focus:outline-none xs:text-sm transition-all ${className} ${
                    error ? errorClasses : normalClasses
                }`}
                onChange={onChange}
                onInvalidCapture={(e) =>
                    (e.target as HTMLInputElement).setCustomValidity(validationError)
                }
                onInput={(e) => (e.target as HTMLInputElement).setCustomValidity('')}
            />
            {error && (
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                    <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
                </div>
            )}
        </div>
    )
}
